import { useState, useEffect } from 'react';
import { ExternalLink, Plus } from 'lucide-react';
import { getOrgVerification } from '../orgVerificationService';
import { formatRelativeTime } from '../utils/timeUtils';
import RequestBadgeModal from './RequestBadgeModal';

/**
 * OrgVerificationBadge - Displays verified organization information for addresses
 *
 * Can display:
 * - Organization badge (orgName, logoUrl) - shows verified organization
 * - Request badge link - when no badge exists (can be disabled with showRequestButton prop)
 *
 * CONSISTENCY STANDARDS (matches UserBadges):
 * - Uses same styling pattern: px-2 py-1, border-{color}-400, rounded-full, text-xs
 * - Green color scheme: from-green-50 to-emerald-50, border-green-400, text-green-700
 *
 * RESPONSIVE BEHAVIOR:
 * - Badge label truncates on mobile (max-w-[120px]), full width on desktop
 * - Tooltip center-aligned on mobile, right-aligned on desktop (prevents overflow)
 */
const OrgVerificationBadge = ({ address, className = '', variant = 'compact', showRequestButton = true, chain = null }) => {
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const loadVerification = async () => {
      if (!address) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getOrgVerification(address, chain);
        setVerification(data);
      } catch (error) {
        console.error('Error loading verification:', error);
        setVerification(null);
      } finally {
        setLoading(false);
      }
    };

    loadVerification();
  }, [address, chain]);

  // Don't render anything if loading or no address
  if (loading || !address) {
    return null;
  }

  // Check if we have organization info to display
  const hasBadge = verification && (verification.orgName || verification.logoUrl);

  // If no badge, show request link (only in compact variant and if showRequestButton is true)
  if (!hasBadge && variant === 'compact' && showRequestButton) {
    return (
      <>
        <button
          onClick={() => setShowModal(true)}
          className={`inline-flex items-center gap-1.5 px-2 py-1 bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-full text-xs whitespace-nowrap transition-colors ${className}`}
        >
          <Plus className="w-3.5 h-3.5 text-gray-600" />
          <span className="font-medium text-gray-700">Request Org Badge</span>
        </button>
        <RequestBadgeModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          address={address}
          chain={chain}
        />
      </>
    );
  }

  // Don't render if no badge
  if (!hasBadge) {
    return null;
  }

  // Compact variant - small badge with tooltip
  return (
    <div
      className="relative inline-flex items-center gap-1.5 px-1.5 py-0.5 bg-gradient-to-r from-yellow-50 to-amber-50 border 
                    border-yellow-400 rounded-full text-xs whitespace-nowrap"
      onMouseEnter={() => setShowTooltip(false)}  // Disable tooltip for now
      onMouseLeave={() => setShowTooltip(false)}
      title={verification.orgName || 'Unknown Entity'}
    >
      {/* Organization Logo */}
      {verification.logoUrl && (
        <img
          src={verification.logoUrl}
          alt={`${verification.orgName || 'Unknown Entity'} logo`}
          className="h-4 w-4 object-contain rounded flex-shrink-0"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      )}

      {/* Organization Name - truncate on narrow screens */}
      <span className="font-medium text-yellow-700 max-w-[180px] sm:max-w-[300px] text-[12px] truncate">
        {verification.orgName || 'Unknown Entity'}
      </span>

      {/* Shield Icon
      <ShieldCheck className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
      */}

      {showTooltip && (
        <div
          className="absolute z-50 pt-2 top-full left-0 sm:left-1/2 sm:-translate-x-1/2"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <div className="p-3 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden" style={{width: 'min(280px, 90vw)', maxWidth: '280px'}}>
          <div className="text-xs space-y-2">
            {verification.logoUrl && (
              <div className="flex justify-center mb-2">
                <img
                  src={verification.logoUrl}
                  alt={`${verification.orgName || 'Organization'} logo`}
                  className="h-12 w-12 object-contain rounded"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
            {verification.orgName && (
              <div>
                <div className="font-semibold text-gray-700">Organization</div>
                <div className="text-gray-600 whitespace-normal break-words" style={{overflowWrap: 'anywhere'}}>{verification.orgName}</div>
              </div>
            )}
            {verification.homepage && (
              <div>
                <div className="font-semibold text-gray-700">Homepage</div>
                <a
                  href={verification.homepage}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 flex items-center gap-1 break-all"
                  onClick={(e) => e.stopPropagation()}
                  style={{overflowWrap: 'anywhere'}}
                >
                  <span className="break-all" style={{overflowWrap: 'anywhere'}}>{verification.homepage}</span>
                  <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </a>
              </div>
            )}
            {verification.claimedAt && (
              <div>
                <div className="font-semibold text-gray-700">Onboarded</div>
                <div className="text-gray-600 whitespace-normal break-words" style={{overflowWrap: 'anywhere'}}>{formatRelativeTime(verification.claimedAt)}</div>
              </div>
            )}
          </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgVerificationBadge;
